import { Subscription, SubscriptionLabel } from "@custom-types/subscription-types";
import { SUBSCRIPTION_PRIORITY } from "@src/constants/subscription-constants";
import { SphereDashboardAPITypes } from "@stellar/api-logic";

/**
 * @returns whether the passed company subscription is of type Subscription
 * @param data Company subscription
 */
export function isXGSubscription(
  data: SphereDashboardAPITypes.IGetCompanySubscriptionResponse
): data is Subscription {
  const subscriptionLabelSet = new Set<string>(Object.values(SubscriptionLabel));
  return subscriptionLabelSet.has(data.label);
}

/**
 * Compares two subscriptions for sorting.
 * Subscriptions are sorted primarily by their priority (higher priority first),
 * and secondarily by their end date (later end date first) if priorities are equal.
 *
 * @param a - The first subscription to compare.
 * @param b - The second subscription to compare.
 * @returns A negative number if `a` should come before `b`,
 *          a positive number if `b` should come before `a`,
 *          or 0 if they are considered equal.
 */
export function compareSubscriptionsByPriority(a: Subscription, b: Subscription): number {
  const priorityA = SUBSCRIPTION_PRIORITY[a.label];
  const priorityB = SUBSCRIPTION_PRIORITY[b.label];

  // If priorities are different, sort by priority in descending order
  if (priorityB !== priorityA) {
    return priorityB - priorityA;
  }
  
  // If priorities are the same, sort by later end date first
  const endDateA = new Date(a.endDate).getTime();
  const endDateB = new Date(b.endDate).getTime();
  return endDateB - endDateA;
}

/**
 * Returns whether the subscription is a trial
 */
export function isTrialSubscription(subscription: Subscription | undefined): boolean{
  if (!subscription || !subscription.label) {
    return false;
  }
  return subscription.label.toLowerCase().includes("trial");
}
